// React core and hooks
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";

// Third-party libraries
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

// Material-UI components
import InfoIcon from "@mui/icons-material/Info";
import {
  alpha,
  Box,
  Checkbox,
  FormControlLabel,
  Pagination,
  PaginationItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid'; // MUI v7 Grid (Grid2 API)

// Application components and utilities
import ButtonVitec from '../../../app/components/ButtonVitec';
import ToolTipTfWrapper from '../../../app/components/ToolTipTfWrapper';
import Utils from '../../../felles/Utils';

// Models and types
import { default as NV03Valf, default as NV03Vare } from '../../../models/NV03Vare';
import PageImpl from '../../../models/PageImpl';
import NV03FormData from '../../../models/payload/NV03FormData';
import { ProductProcessingFlags } from '../../../models/ProductProcessingFlags';
import { ProductRemarkFlags } from '../../../models/ProductRemarkFlags';

// Redux actions and state
import * as actions from '../../../store/actions/index';
import * as actionsNV03 from '../../../store/actions/indexNV03';
import { AppState } from '../../../store/rootReducer';

// Child components
import NV03ListHead, { hInit } from './NV03ListHead';
import { TableHeaders } from '../../../models/Headers';


// Define custom styles using makeStyles
const useStyles = makeStyles()((theme: Theme) => ({
  // Root container styling
  root: { margin: 20, fontSize: '1em !important', color: '#fff' },
  container: {
    border: 'none',
    "&.label.Mui-focused ": { color: 'white', }
  },
  table: { minWidth: 700, fontSize: 200, borderRadius: '5px', },
  tC: {
    paddingLeft: 20, textAlign: 'left', fontSize: 16,
  },
  tCB: {
    paddingLeft: 20, textAlign: 'left', fontSize: 16, color: 'RED',
  },
  ba: { border: 'none', marginTop: 10 },
  bb: { color: '#2E2D2C' },
  tField: {
    display: 'flex',
    backgroundColor: alpha('#fff', 0.4),
    // border: '2px solid orange',
    '& label': { fontSize: 20, height: 45, lineHeight: .8, backgroundColor: '1px solid #4f4d4c', },
    height: 45,
    //'& label.Mui-focused': { top: 12, animationIterationCount: 1, transform: 'translate(0, -10px) scale(1)', fontSize: '.75em', color: '#d30535', },
    //'& .MuiInputLabel-formControl': { top: 11, left: 6, position: 'absolute', transform: 'translate(0, 0) scale(1)', color: '#d30535', },
    '& .MuiInput-underline:after': { height: 45, width: 0, fontSize: 20 },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      fontSize: 20,
      border: '1px solid #c4c4c4',
    },
    // (Note: space or no space after `&` matters. See SASS "parent selector".)
    '& .MuiOutlinedInput-root': {
      color: 'black',
      height: 47,
      fontSize: 20,

      // - The Input-root, inside the TextField-root
      '& fieldset': {
        border: '2px solid white',
        /* - The <fieldset> inside the Input-root */
      },
      '&:hover fieldset': {},
      '&.Mui-focused ': {
        backgroundColor: alpha('#fff', 0.8),
      },
    },
  },
}),
);


// Define props for the NV03FormList component
interface NV03VALFFormHookProps {
  size: {
    readonly width: number | null;
    readonly height: number | null;
  };
}

// Main functional component
const NV03FormList: React.FC<NV03VALFFormHookProps> = ({ size }) => {
  const { classes: cls } = useStyles();  // Accessing styles
  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors - Extract data from global state
  const page: PageImpl<NV03Vare> = useSelector((state: AppState) => state.NV03.page);
  const isVeilPrisChecked: boolean = useSelector((state: AppState) => state.NV03.isVeilPrisChecked);
  const isVisAlleLagerChecked: boolean = useSelector((state: AppState) => state.NV03.isVisAlleLagerChecked);
  const tableHeaders: TableHeaders[] = useSelector((state: AppState) => state.NV03.tableHeaders);
  const isLoading: boolean = useSelector((state: AppState) => state.utils.isLoading);
  const keyValuePairs4VSE: { key: string; value: any }[] = useSelector((state: AppState) => state.ui.keyValuePairs4VSE);
  const keyValuePairs4NV03: { key: string; value: any }[] = useSelector((state: AppState) => state.NV03.keyValuePairs4NV03);
  const currentRowFocused: number = useSelector((state: AppState) => state.NV03.currentRowFocused);
  const scrollTopPosition: number = useSelector((state: AppState) => state.NV03.scrollTopPosition);

  // Local state
  const [defaultPageCount, setDefaultPageCount] = useState(10);
  const [currentFocusedField] = useState(''); // Track which input field has focus
  const [isHentDisabled, setIsHentDisabled] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [hasTableHeadersBeenVisible, setHasTableHeadersBeenVisible] = useState(false);

  // Refs
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const tooltipContent = (
    <Box>
      {/* Section 1 */}
      <Box sx={{ padding: "5px", fontWeight: "bold" }}>
        Programmet er en ny versjon av VALF og andre programmer for varespørring.
      </Box>
      <Typography variant="body2" sx={{ padding: "5px" }}>
        Søk kan gjøres på en eller flere søkekriterier. <br />
        <strong>Alfa</strong> – Ved søk på alfa med to karakterer taster du mellomrom-tasten som siste karakter. <br />
        <strong>Artikkel</strong> – Listes stigende fra begynnelses av ditt søk. <br />
        <strong>Varebetegnelse</strong> – Listes stigende fra begynnelsen av ditt søk. <br />
        <strong>Varegruppe</strong> – Lister alt i denne varegruppen. <br />
        Eksempel: Ønsker du alt med en alfakode taster du kun inn alfakode og deretter Enter eller Hent- knappen. <br />
      </Typography>



      <Box sx={{ padding: "5px" }}><strong>På linjen under kan du i tillegg disse valgene:</strong><br />
        Kun med beholdning (totalbeholdning) – Da blir varer uten beholdning fjernet fra listen
        Vis alle lager – Da ser du beholdning på andre lager. Tallet i parentes vises antall lager med beholdninger på ditt søk.
        Veil. Pris – Viser Veil. Pris feltet fra VAVE.
        Kun med lokasjon – Da vises kun varer som har lokasjon i ditt søk</Box>



      <Box sx={{ padding: "5px" }}><strong>Søkeresultat:</strong><br />
        Utlisting vises stigende på alfa og artikkel. 150 artikler på hver side.
        Varer med minusbeholdning vises med rød skrift.
        Varer som er slettemerket i VAVE vises med rød bakgrunn.
        Beholdning – Dette er totalbeholdning. Lager 10 vises ikke som eget lager. Men har du ikke noe på de andre lagrene er Beholdning samme som lager 10.</Box>


      <Box sx={{ padding: "5px" }}>
        <strong>Overgang til VAVE:</strong><br />
        Gå ned på ønsket linje og tast F4. Du kan nå bla med F7 og F8 for artikkel før og etter i ditt søk i VALF. F3 fra VAVE går tilbake til nye VALF.</Box>


      <Box sx={{ padding: "5px" }}><strong>Filterknapper:</strong><br />
        Ved å trykke på linjen med overskrift ser du at filterknappen «snur seg» du kan nå søke på innhold i feltet. Trykk Enter for å aktivere ditt søk.
        For å oppheve filter søket trykker du på filteret slik at det «snur seg» tilbake.
        Tips: I feltet for beholdning kan du søke på varer med minusbeholdning ved å filtrere på – (minustegnet).</Box>



      <Box sx={{ padding: "5px" }}><strong>Excel fil:</strong><br />
        Ved å klikke på grønn Excel knapp får du ditt søk på fil.
        Filen inneholder flere felter fra VAVE enn hva som vises på skjerm. Blant annet rebestilling punkter.
        NB. For Selvkost på filen må din bruker ha tilgang til gruppe 17 – Innkjøp.</Box>




    </Box>
  );


  // React Hook Form configuration with default values
  const methods = useForm<NV03FormData>({
    defaultValues: {
      alfaKode: '',
      artikkel: '',
      varebetegnelse: '',
      varegruppe: '',
      page: 0,
      pageSize: 150,
      sort: 'artikkel',
      ckBehold: false,
      ckVisAlle: false,
      ckVeilpris: false,
      ckLokasjon: false,
      exportToExcel: false,
      tableHeaders: []
    },
  });


  {/* --------------------------------------- Effects ------------------------------------- */ }

  // Effect: Setup scroll tracking and keyboard event listeners
  // This effect runs once on mount and cleans up on unmount
  useEffect(() => {
    // Handler to track scroll position for restoring state when navigating back
    const handleScroll = () => {
      if (tableContainerRef.current) {
        setScrollPosition(tableContainerRef.current.scrollTop);
      }
    };

    // Create debounced scroll handler to prevent performance issues
    const debouncedScrollHandler = Utils.debounce_new(handleScroll, 150);

    // Add window-level keyboard event listener for global shortcuts
    window.addEventListener('keydown', handleKeyUpPressedAtWindowDocumentLevel);

    // Setup scroll tracking on table container
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', debouncedScrollHandler);
      // Restore previous scroll position if available
      tableContainer.scrollTop = scrollTopPosition;
    }

    // Cleanup function: Remove all event listeners to prevent memory leaks
    return () => {
      window.removeEventListener('keydown', handleKeyUpPressedAtWindowDocumentLevel);
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', debouncedScrollHandler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount/unmount



  // Effect: Initialize component and handle navigation/data restoration
  // Split into separate effects for better performance and clarity
  useEffect(() => {
    // Update page count when search results change
    setDefaultPageCount(page.content?.length > 0 ? page.totalPages : 10);
  }, [page.totalPages, page.content]);

  useEffect(() => {
    // Reset UI state on mount
    dispatch(actions.setSandwichButtonStateClicked(false));

    // Initialize column headers if not loaded
    if (tableHeaders.length === 0) {
      dispatch(actionsNV03.NV03UpdateColumnHeaderList(hInit));
    }

    // Cleanup: Remove touch event listener (tablet compatibility)
    document.body.removeEventListener('touchmove', Utils.preventDefault);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect: Auto-focus first field when no data is present
  useEffect(() => {
    if (currentFocusedField === '' && page.content?.length === 0) {
      const inputField = document.querySelector<HTMLInputElement>('input[name="alfaKode"]');
      if (inputField && inputField.value === '') {
        inputField.focus();
      }
    }
  }, [currentFocusedField, page.content?.length]);

  // Effect: Handle keyboard shortcuts (Arrow keys, F4, Enter, Escape)
  useEffect(() => {
    const keyUpHandler = (event: KeyboardEvent) => {
      handleKeyUpPressedAtDocumentLevel(event as KeyboardEventWithValue);
    };

    document.addEventListener('keyup', keyUpHandler);

    return () => {
      document.removeEventListener('keyup', keyUpHandler);
    };
  }, [currentRowFocused, page.content]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect: Navigate to VSE when redirect data is ready
  useEffect(() => {
    if (keyValuePairs4VSE.length > 0) {
      navigate(Utils.HOMEPAGE_CONTEXT + '/vse?transid=VAVE');
    }
  }, [keyValuePairs4VSE, navigate]);

  // Effect: Restore form state from NV03 key-value pairs
  useEffect(() => {
    if (keyValuePairs4NV03.length > 0) {
      keyValuePairs4NV03.forEach((element: { key: string; value: any }) => {
        methods.setValue(element.key as keyof NV03FormData, element.value);
      });

      // Auto-submit form if enabled
      if (isHentDisabled) {
        setIsHentDisabled(false);
        methods.handleSubmit(onSubmit)();
        dispatch(actionsNV03.setKeyValuePairs4NV03([]));
      }
    }
  }, [keyValuePairs4NV03]); // eslint-disable-line react-hooks/exhaustive-deps





  /**
   * Handles window-level keyboard shortcuts (F7, F8, F9, Home)
   * F7: Previous page, F8: Next page, F9: Reset form
   */
  const handleKeyUpPressedAtWindowDocumentLevel = useCallback((event: KeyboardEvent): void => {
    const { key } = event;

    // Only handle specific function keys
    if (!['Home', 'F7', 'F8', 'F9'].includes(key)) {
      return;
    }

    event.preventDefault();

    switch (key) {
      // F7: Navigate to previous page
      case 'F7':
        if (methods.getValues('page') >= 1) {
          methods.setValue('page', methods.getValues('page') - 1);
          methods.handleSubmit(onSubmit)();
        }
        break;

      // F8: Navigate to next page
      case 'F8':
        if (methods.getValues('page') < defaultPageCount - 1) {
          methods.setValue('page', methods.getValues('page') + 1);
          methods.handleSubmit(onSubmit)();
        }
        break;

      // F9: Reset form to initial state
      case 'F9': {
        methods.reset();
        dispatch(actionsNV03.NV03ResetForm(true));
        setHasTableHeadersBeenVisible(false);

        // Refocus on first input field
        const inputField = document.querySelector<HTMLInputElement>('input[name="alfaKode"]');
        if (inputField && inputField.value === '') {
          inputField.focus();
        }
        break;
      }
    }
  }, [defaultPageCount, dispatch, methods]); // eslint-disable-line react-hooks/exhaustive-deps




  /**
   * Type definitions for keyboard event handlers
   */
  interface KeyboardEventWithValue extends KeyboardEvent {
    target: EventTarget & { value?: string };
  }

  interface NV03FormListPageContentItem {
    id: number;
    [key: string]: any;
  }

  /**
   * Handles document-level keyboard navigation (Arrow keys, Escape, F4, Enter)
   * - Arrow Up/Down: Navigate between table rows
   * - Escape: Clear row selection and return to search
   * - F4/Enter (when row selected): Open item in VSE (VAVE) editor
   */
  const handleKeyUpPressedAtDocumentLevel = useCallback((event: KeyboardEventWithValue): void => {
    const { key } = event;
    const actionSource: string = key || event.target.value || '';

    // Only handle specific navigation keys — do NOT preventDefault for other keys (e.g. Backspace)
    if (!['Escape', 'ArrowUp', 'ArrowDown', 'F4', 'Enter'].includes(actionSource)) {
      return;
    }

    event.preventDefault();

    // Find current row index in the data
    const currentIndex: number = page.content.findIndex(
      (item: NV03FormListPageContentItem) => item.id === currentRowFocused
    );

    switch (actionSource) {
      // Escape: Clear selection and return focus to search
      case 'Escape': {
        dispatch(actionsNV03.setCurrentRowFocused(-1));
        const inputField = document.querySelector<HTMLInputElement>('input[name="alfaKode"]');
        inputField?.focus();
        break;
      }

      // Arrow Up: Navigate to previous row (wraps to last row)
      case 'ArrowUp': {
        if (page.content.length > 0) {
          const previousRow = currentIndex > 0
            ? page.content[currentIndex - 1]
            : page.content[page.content.length - 1];
          dispatch(actionsNV03.setCurrentRowFocused(previousRow?.id || 0));
        }
        break;
      }

      // Arrow Down: Navigate to next row (wraps to first row)
      case 'ArrowDown': {
        if (page.content.length > 0) {
          const nextRow = currentIndex < page.content.length - 1
            ? page.content[currentIndex + 1]
            : page.content[0];
          dispatch(actionsNV03.setCurrentRowFocused(nextRow?.id || 0));
        }
        break;
      }

      // F4 or Enter (with row selected): Open item in VSE editor
      case 'F4':
      case 'Enter': {
        if (currentRowFocused === -1 || page.content.length === 0) {
          return;
        }

        const selectedItem = page.content.find((n: NV03Vare) => n.id === currentRowFocused);
        if (selectedItem) {
          dispatch(actionsNV03.NV03SetResetFormEnabled(false));
          dispatch(actions.setURLCallBack('NV03'));

          // Prepare navigation parameters for VSE
          const navigationParams = [
            { key: 'productId', value: selectedItem.id },
            { key: 'transid', value: 'VAVE' },
            { key: 'SKODE', value: 'X' },
            { key: 'SALFA', value: selectedItem.alpha },
            { key: 'SARTNR', value: selectedItem.productNumber.trim() },
            { key: 'SEDBNR', value: selectedItem.edbNumber },
            { key: 'VGR', value: selectedItem.productGroup }
          ];
          dispatch(actions.setKeyValuePairs4VSE(navigationParams));
        }
        break;
      }

      default:
        break;
    }
  }, [currentRowFocused, dispatch, page.content]);

  /**
   * Handles row click/selection in the data table
   * Saves scroll position and sets the focused row for keyboard navigation
   */
  interface HandleRowSelectionEvent extends React.MouseEvent<HTMLTableRowElement, MouseEvent> { }

  const handleRowSelection = useCallback((_event: HandleRowSelectionEvent, item: NV03Vare): void => {
    dispatch(actionsNV03.setScrollTopPosition(scrollPosition));
    dispatch(actionsNV03.setCurrentRowFocused(item.id));
  }, [dispatch, scrollPosition]);

  /**
   * Validates if search form has minimum required input
   * At least one field must meet minimum length requirements
   */
  const validateSearchInput = useCallback((): boolean => {
    return (
      methods.getValues('alfaKode').length >= 2 ||
      methods.getValues('artikkel').length >= 3 ||
      methods.getValues('varebetegnelse').length >= 3 ||
      methods.getValues('varegruppe').length >= 3
    );
  }, [methods]);

  /**
   * Handles keyboard shortcuts and form actions (Enter, Reset, Export)
   */
  interface HandleKeyPressEvent extends React.KeyboardEvent<HTMLFormElement> {
    target: EventTarget & { value?: string };
  }

  const handleKeyPress = useCallback((event: HandleKeyPressEvent): void => {
    const { key } = event;
    const actionSource: string = key || event.target.value || '';

    // Update button disabled state based on validation
    setIsHentDisabled(!validateSearchInput());

    // Only preventDefault for keys this handler actually processes
    if (!['Enter', 'Export', 'Reset'].includes(actionSource)) {
      return;
    }

    event.preventDefault();

    switch (actionSource) {
      // Enter or Export: Submit form if validation passes
      case 'Enter':
      case 'Export':
        if (!isHentDisabled) {
          methods.setValue('exportToExcel', actionSource === 'Export');
          methods.setValue('page', 0);
          methods.setValue('tableHeaders', tableHeaders);
          methods.handleSubmit(onSubmit)();
        }
        break;

      // Reset: Clear form and restore initial state
      case 'Reset': {
        methods.reset();
        dispatch(actionsNV03.setCurrentRowFocused(-1));
        dispatch(actionsNV03.NV03ResetForm(true));
        dispatch(actionsNV03.NV03UpdateColumnHeaderList(hInit));
        dispatch(actionsNV03.setKeyValuePairs4NV03([]));
        setIsHentDisabled(true);
        setHasTableHeadersBeenVisible(false);

        // Refocus on first input field
        const inputField = document.querySelector<HTMLInputElement>('input[name="alfaKode"]');
        if (inputField && inputField.value === '') {
          inputField.focus();
        }
        break;
      }
      default:
        break;
    }
  }, [dispatch, isHentDisabled, methods, validateSearchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Form submission handler
   * Validates input, prepares data, and dispatches API request
   */
  const onSubmit = useCallback((data: NV03Valf | any) => {
    // Validate at least one search field has sufficient input
    if (!validateSearchInput()) {
      return;
    }

    // Ensure headers are initialized
    if (!data.tableHeaders || data.tableHeaders.length === 0) {
      data.nV03Headers = hInit;
    }

    // Store search parameters for state restoration
    const searchParams = [
      { key: 'alfaKode', value: methods.getValues('alfaKode') },
      { key: 'artikkel', value: methods.getValues('artikkel') },
      { key: 'varebetegnelse', value: methods.getValues('varebetegnelse') },
      { key: 'varegruppe', value: methods.getValues('varegruppe') }
    ];
    dispatch(actionsNV03.setKeyValuePairs4NV03(searchParams));

    // Prevent duplicate submissions while loading
    if (!isLoading) {
      dispatch(actionsNV03.NV03VALFListeGet(data));
      methods.setValue('exportToExcel', false);
    }
  }, [dispatch, isLoading, methods, validateSearchInput]);

  /**
   * Pagination change handler
   * @param pageValue - New page number (1-indexed from UI, converted to 0-indexed)
   */
  const pagechange = useCallback((_e: any, pageValue = 0) => {
    methods.setValue('page', pageValue - 1); // Convert to 0-indexed
    methods.handleSubmit(onSubmit)();
  }, [methods, onSubmit]);

  /**
   * Column sorting handler
   * Updates headers with sort configuration and triggers data refresh
   */
  const setSort = useCallback((value: TableHeaders[]) => {
    methods.setValue('tableHeaders', value);
    methods.handleSubmit(onSubmit)();
  }, [methods, onSubmit]);

  /**
   * Helper function to find header configuration by column ID
   */
  const getObjectById = useCallback((id: string): TableHeaders | undefined => {
    return tableHeaders.find(row => row.id === id);
  }, [tableHeaders]);

  /**
   * Auto-advance to next field when current field reaches max length
   * Improves UX by automatically moving focus
   */
  interface GoToNextFieldEvent {
    value: string;
    getAttribute: (attr: string) => string | null;
  }

  const goToNextField = useCallback((e: GoToNextFieldEvent, nextFieldName: string): void => {
    const maxLength = Number(e.getAttribute('maxlength'));

    // Auto-advance when field is full
    if (e.value.length === maxLength) {
      const inputField = document.querySelector<HTMLInputElement>(`input[name="${nextFieldName}"]`);
      inputField?.focus();
    }
  }, []);

  /**
   * Handles spacebar toggle for checkboxes (accessibility feature)
   * Allows keyboard users to toggle checkboxes with spacebar
   */
  interface ToggledWithSpaceBarEvent extends React.KeyboardEvent<HTMLButtonElement> {
    currentTarget: HTMLButtonElement & {
      querySelector: (selectors: string) => HTMLInputElement | null;
    };
  }

  const toggledWithSpaceBar = useCallback((event: ToggledWithSpaceBarEvent): void => {
    const { key } = event;
    const inputName = event.currentTarget.querySelector("input")?.name as keyof NV03FormData | undefined;

    // Toggle checkbox value when spacebar is pressed
    if (inputName && key === ' ') {
      const currentValue = methods.getValues(inputName);
      methods.setValue(inputName, !currentValue);
      dispatch(actionsNV03.setIsBeholdningCheckboxState(!currentValue));
      methods.handleSubmit(onSubmit)();
    }
  }, [dispatch, methods, onSubmit]);

  /**
   * Checks if any column filter is currently active
   * Used to determine if Reset button should be enabled
   */
  const hasHearderListAFilterActive = useCallback((): boolean => {
    return tableHeaders.some((header) => header.filter !== '');
  }, [tableHeaders]);

  useEffect(() => {
    if (page.content.length > 0 || hasHearderListAFilterActive()) {
      setHasTableHeadersBeenVisible(true);
    }
  }, [page.content.length, hasHearderListAFilterActive]);

  // Rendering the form and table
  return (
    <FormProvider {...methods}>
      <form className={cls.root} onKeyUp={handleKeyPress}>
        <input  {...methods.register("page")} hidden />
        <input  {...methods.register("pageSize")} hidden />
        <input  {...methods.register("sort")} hidden />
        <input  {...methods.register("exportToExcel")} hidden />
        <Paper elevation={0} sx={{ backgroundColor: alpha('#fff', .6) }} >
          <TableContainer sx={{ borderRadius: '5px', display: 'flex', flexDirection: 'column', height: page.content.length > 0 ? 'calc(100vh - 122px)' : 'auto', maxHeight: '100vh' }} >
            <Grid container width={'100%'} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              {/* HEADER SECTION */}
              <Grid container sx={{ flexShrink: 0 }} justifyContent='left' className={cls.ba} position="relative" >
                {/* Info Tooltip at top right */}
                <Box sx={{ position: 'absolute', top: '-4px', right: '12px', zIndex: 1 }}>
                  <Tooltip
                    title={<Typography variant="body2">{tooltipContent}</Typography>}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 800,
                          marginRight: 1,
                          fontSize: '14px'
                        }
                      }
                    }}
                  >
                    <InfoIcon style={{ cursor: "pointer", color: "#1d6f42" }} />
                  </Tooltip>
                </Box>

                <Grid container className={cls.bb} rowSpacing={0} columnSpacing={3} direction="row"
                  justifyContent="left" alignItems="center" sx={{ flexWrap: 'wrap', paddingTop: '6px', paddingBottom: '6px' }}>
                  <Grid container alignItems="center" justifyContent='left' className={cls.bb} />
                  <Grid />
                  {/* Alfa Code Field - Alphabetic product group code (0-3 characters) */}
                  <Grid size={1} sx={{ minWidth: '100px' }}>
                    
                      <TextField label="Alfa" autoComplete="off" className={cls.tField} inputProps={{ maxLength: 3, shrink: 'false' }}
                        {...methods.register("alfaKode", { required: false })}
                        onClick={() => dispatch(actionsNV03.setCurrentRowFocused(-1))}
                        onChange={(e) => {
                          dispatch(actionsNV03.setCurrentRowFocused(-1));
                          goToNextField(e.target, 'artikkel');
                          methods.setValue('alfaKode', e.target.value.toUpperCase());
                        }}
                      />
                  
                  </Grid>

                  {/* Article Number Field - Product/article number search */}
                  <Grid size={3} sx={{ minWidth: '200px' }}>
                   
                      <TextField label="Artikkel" autoComplete="off" className={cls.tField}
                        {...methods.register("artikkel", { required: false })}
                        onClick={() => dispatch(actionsNV03.setCurrentRowFocused(-1))}
                        onChange={(e) => {
                          dispatch(actionsNV03.setCurrentRowFocused(-1));
                          methods.setValue('artikkel', e.target.value);
                        }}
                      />
                   
                  </Grid>

                  {/* Product Description Field */}
                  <Grid size={2}>
                    
                      <TextField label="Varebetegnelse" autoComplete="off" className={cls.tField}
                        {...methods.register("varebetegnelse", { required: false })}
                        onClick={() => dispatch(actionsNV03.setCurrentRowFocused(-1))}
                        onChange={(e) => {
                          dispatch(actionsNV03.setCurrentRowFocused(-1));
                          methods.setValue('varebetegnelse', e.target.value);
                        }}
                      />
                
                  </Grid>

                  {/* Product Group Field */}
                  <Grid size={2}>
                   
                      <TextField label="Varegruppe" autoComplete="off" className={cls.tField}
                        {...methods.register("varegruppe", { required: false })}
                        onClick={() => dispatch(actionsNV03.setCurrentRowFocused(-1))}
                        onChange={(e) => {
                          dispatch(actionsNV03.setCurrentRowFocused(-1));
                          methods.setValue('varegruppe', e.target.value);
                        }}
                      />
                   
                  </Grid>
                  {/* Submit Button - Trigger search */}
                  <Grid>
                    <div style={{ marginBottom: 10 }}>
                      <ButtonVitec
                        _onclick={() =>
                          handleKeyPress({
                            key: 'Enter',
                            target: { value: 'Enter' },
                            preventDefault: () => {},
                          } as any)
                        }
                        _width={105}
                        _height={37}
                        _weight={600}
                        _type={'button'}
                        _label={'HENT'}
                        _value={'Enter'}
                        disabled={isHentDisabled}
                      />
                    </div>
                  </Grid>
                  <Grid size="auto" />
                </Grid>
                {/* CHECKBOX SECTION - Filter options */}
                <Grid container className={cls.bb} rowSpacing={0} columnSpacing={3} direction="row"
                  justifyContent="left" alignItems="center" sx={{ flexWrap: 'wrap', paddingTop: '4px', paddingBottom: '4px' }}>
                  <Grid />

                  {/* Checkbox: Only show items with stock */}
                  <Grid size={1.05} sx={{ minWidth: '200px' }}>
                    <FormControlLabel sx={{ width: '220px' }} label={"Kun med beholdning"}
                      control={<Controller control={methods.control}
                        name="ckBehold" render={({ field: { onChange, value } }) => (
                          <ToolTipTfWrapper id='ckBehold'
                            title={<div> Vil kun vise data der beholdningsverdien ikke er lik 0.</div>} placement={"top"} delay={8000}>
                            <Checkbox onKeyUp={toggledWithSpaceBar} onChange={(_event, checked) => {
                              onChange(checked);
                              dispatch(actionsNV03.setIsBeholdningCheckboxState(checked));
                              methods.handleSubmit(onSubmit)();
                            }} name={'ckBehold'} checked={value} />
                          </ToolTipTfWrapper>
                        )}
                      />} />
                  </Grid>

                  {/* Checkbox: Show all warehouses (displays count of visible warehouses) */}
                  <Grid size={1.05} sx={{ minWidth: '140px' }}>
                    <FormControlLabel sx={{ width: '220px' }}
                      label={`Vis alle lager${tableHeaders.filter(h => ['lk18', 'lk13', 'lk15', 'lk17', 'lk92', 'lk93'].includes(h.id) && h.vis === 1).length > 0 ? ` (${tableHeaders.filter(h => ['lk18', 'lk13', 'lk15', 'lk17', 'lk92', 'lk93'].includes(h.id) && h.vis === 1).length})` : ''}`}
                      control={<Controller control={methods.control}
                        name="ckVisAlle" render={({ field: { onChange, value } }) => (
                          <Checkbox onKeyUp={toggledWithSpaceBar} onChange={(_event, checked) => {
                            onChange(checked);
                            dispatch(actionsNV03.setIsVisAlleLagerCheckboxState(checked));
                            methods.handleSubmit(onSubmit)();
                          }} name={'ckVisAlle'} checked={value || false} />
                        )}
                      />} />
                  </Grid>

                  {/* Checkbox: Show recommended price column */}
                  <Grid size={0.85} sx={{ minWidth: '100px' }}>
                    <FormControlLabel sx={{ width: '120px' }} label="Veil. pris"
                      control={<Controller control={methods.control}
                        name="ckVeilpris" render={({ field: { onChange, value } }) => (
                          <Checkbox onKeyUp={toggledWithSpaceBar} onChange={(_event, checked) => {
                            onChange(checked);
                            dispatch(actionsNV03.setIsVeilPrisCheckboxState(checked));
                            methods.handleSubmit(onSubmit)();
                          }} name={'ckVeilpris'} checked={value} />
                        )}
                      />} />
                  </Grid>

                  {/* Checkbox: Only show items with location */}
                  <Grid size={1} sx={{ minWidth: '190px' }}>
                    <FormControlLabel sx={{ width: '190px' }} label="Kun med lokasjon"
                      control={<Controller control={methods.control}
                        name="ckLokasjon" render={({ field: { onChange, value } }) => (
                          <Checkbox onKeyUp={toggledWithSpaceBar} onChange={(_event, checked) => {
                            onChange(checked);
                            dispatch(actionsNV03.setIsLocationCheckboxState(checked));
                            methods.handleSubmit(onSubmit)();
                          }} name={'ckLokasjon'} checked={value} />
                        )}
                      />} />
                  </Grid>
                  {/****************************************************************  Action Buttons: Excel, Nullstill, Valf */}
                  <Grid sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, alignItems: 'center', paddingLeft: '10px', marginLeft: '-30px', marginTop: '-15px' }}>
                    <ButtonVitec _onclick={() =>
                        handleKeyPress({
                          key: 'Export',
                          target: { value: 'Export' },
                          preventDefault: () => {},
                        } as any)
                      } _width={105} _height={37} _type={'button'} _color={'green'}
                      _label={'EXCEL'} _value={'Export'} disabled={page.content.length === 0 || isHentDisabled} />
                    <ButtonVitec _onclick={() =>
                        handleKeyPress({
                          key: 'Reset',
                          target: { value: 'Reset' },
                          preventDefault: () => {},
                        } as any)
                      } _width={105} _height={37} _weight={600} _type={'button'}
                      _label={'NULLSTILL'} _value={'Reset'}
                      disabled={false}
                      _color={'red'} />
                    <ButtonVitec _onclick={() => navigate(Utils.HOMEPAGE_CONTEXT + '/vse?transid=VALF')}
                      _width={105} _height={37} _weight={600} _type={'button'}
                      _label={'VALF'} _color={'red'} />
                  </Grid>
                  <Grid size="auto" />
                </Grid>
                <Grid container height={'5px'} alignItems="center" justifyContent='left' className={cls.bb} />
              </Grid>
              {/* CONTENT SECTION - Table */}
              <Grid container
                alignItems="top"
                justifyContent='center'
                className={cls.ba}
                ref={tableContainerRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: page.content.length > 0 ? 'auto' : 'visible',
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#706c6a", },
                }}
              >
                {page.content.length > 0 || hasHearderListAFilterActive() || hasTableHeadersBeenVisible ?
                  <Grid size="grow" >
                    <Table stickyHeader className={cls.table} size="small" aria-labelledby="tableTitle" >
                      <NV03ListHead onRequestSort={setSort} pageContent={page.content} />
                      <TableBody sx={{ width: '100%' }}>
                        {
                          Array.isArray(page.content) && page.content?.length > 0 ? (
                            page.content.map((n: NV03Vare) => (
                              <TableRow
                                aria-checked={false}
                                key={n.id}
                                selected={false}
                                sx={{
                                  backgroundColor:
                                    currentRowFocused === n.id && n.deletedFlag === 'S' ? '#FF9999' :
                                      n.deletedFlag !== '' ? '#FFCCCB' : currentRowFocused === n.id ? '#d8eff2' : '#fff',
                                  border: '1px solid #fff',
                                  '&:hover': {
                                    backgroundColor: currentRowFocused === n.id ? '#d8eff2' : '#f3f3f3', // Change this to your desired hover color
                                  }
                                }}
                                onClick={(event) => { handleRowSelection(event, n) }}
                              >
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC}
                                  sx={{ width: hInit.find(hd => hd.id === 'alfa')?.width }}>{n.alpha}</TableCell>
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'artikkelNummer')?.width }}>{n.productNumber}</TableCell>
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'vareSpes')?.width }}>{n.name}</TableCell>
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'vareGruppe')?.width }}>{n.productGroup}</TableCell>
                                <TableCell sx={{ width: hInit.find(hd => hd.id === 'utSalgPris')?.width, textAlign: 'right', paddingLeft: '20px', fontSize: '16px', color: n.inStock.toString().includes('-') ? 'RED' : 'inherit' }}>{Utils.decimalformatter(n.salesPrice)}</TableCell>
                                {isVeilPrisChecked
                                  ? <TableCell sx={{ width: hInit.find(hd => hd.id === 'veilPris')?.width, textAlign: 'right', paddingLeft: '20px', fontSize: '16px', color: n.inStock.toString().includes('-') ? 'RED' : 'inherit' }}>{Utils.decimalformatter(n.recommendedPrice80)}</TableCell> : null}
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lokasjon')?.width }}>{n.location}</TableCell>
                                {/*isBeholdningChecked ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC}>{n.inStock}</TableCell> : null*/}
                                <TableCell sx={{ width: hInit.find(hd => hd.id === 'beholdning')?.width, textAlign: 'right', paddingLeft: '20px', fontSize: '16px', color: n.inStock.toString().includes('-') ? 'RED' : 'inherit' }}>{n.inStock.toString() !== '0' ? n.inStock : ''}</TableCell>
                                {isVisAlleLagerChecked && getObjectById('lk18')?.vis === 1
                                  ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lk18')?.width, backgroundColor: '#F0F8FF' }}>{n.lk18}</TableCell> : null}
                                {isVisAlleLagerChecked && getObjectById('lk13')?.vis === 1
                                  ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lk13')?.width, backgroundColor: '#F0F8FF' }}>{n.lk13}</TableCell> : null}
                                {isVisAlleLagerChecked && getObjectById('lk15')?.vis === 1
                                  ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lk15')?.width, backgroundColor: '#F0F8FF' }}>{n.lk15}</TableCell> : null}
                                {isVisAlleLagerChecked && getObjectById('lk17')?.vis === 1
                                  ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lk17')?.width, backgroundColor: '#F0F8FF' }}>{n.lk17}</TableCell> : null}
                                {isVisAlleLagerChecked && getObjectById('lk92')?.vis === 1
                                  ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lk92')?.width, backgroundColor: '#F0F8FF' }}>{n.lk92}</TableCell> : null}
                                {isVisAlleLagerChecked && getObjectById('lk93')?.vis === 1
                                  ? <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'lk93')?.width, backgroundColor: '#F0F8FF' }}>{n.lk93}</TableCell> : null}
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'auto')?.width }}>{ProductProcessingFlags[n.processingFlags as keyof typeof ProductProcessingFlags] || ''}</TableCell>
                                <TableCell className={n.inStock.toString().includes('-') ? cls.tCB : cls.tC} sx={{ width: hInit.find(hd => hd.id === 'merknad')?.width }}>{ProductRemarkFlags['FLAG_' + n.remarkFlag as keyof typeof ProductRemarkFlags] || ''}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} className={cls.tC} scope="row" padding="none">
                                No data available
                              </TableCell>
                            </TableRow>
                          )
                        }
                      </TableBody>
                    </Table>
                  </Grid>
                  : null}
              </Grid>
              {/*********************************************************** FOOTER *****************************************************************/}
            </Grid>
          </TableContainer>
          {/* FOOTER SECTION - Pagination */}
          {page.content.length > 0 ?
            <Grid container height={"39px"} alignItems="center" justifyContent='center'
              className={cls.bb} rowSpacing={0} columnSpacing={0}
            >
              <Grid>
                <Pagination sx={{
                  color: 'white',
                  "& .Mui-selected": {
                    backgroundColor: "#fff"
                  }
                }}
                  count={defaultPageCount}
                  onChange={(e, value) => pagechange(e, value)}
                  onKeyUp={(e) => pagechange(e)}
                  page={Math.max(1, (methods.getValues('page') || 0) + 1)}
                  renderItem={(item) => (
                    <PaginationItem sx={{ fontSize: '1.2em', }}
                      {...item}
                    />
                  )}
                />
              </Grid>
            </Grid>
            : null}</Paper>
      </form>
    </FormProvider >
  );
};
// Exporting the NV03FormList component
export default NV03FormList;